import {
  CartSnapshot,
  DecisionSnapshot,
  SnapshotBase,
  SnapshotsGroupedByTrackId,
  UserDecisionPublic,
} from "@/shared/types";
import toast from "react-hot-toast";

export const UNMATCHED_DETECTION_TRACK_ID = "UNMATCHED_DETECTION_TRACK_ID";
export const UNMATCHED_DETECTION_COLOR = "red";
export const CACHE_RESOLUTION = 1000;

export function findClosestTimestamp(
  target: number,
  timestamps: { datetime: string }[]
) {
  let closestTimestamp = 0;
  let closestDiff = Infinity;

  for (let i = 0; i < timestamps.length; i++) {
    const timestamp = new Date(timestamps[i].datetime).getTime();
    const diff = Math.abs(target - timestamp);

    if (diff < closestDiff) {
      closestDiff = diff;
      closestTimestamp = timestamp;
    }
  }

  return new Date(closestTimestamp).toISOString();
}

export function findClosest(currentTime: number, timestamps: SnapshotBase[]) {
  let closesetIndex = -1;

  for (let i = 0; i < timestamps.length; i++) {
    const timestamp = new Date(getDateTime(timestamps[i])).getTime();
    const diff = currentTime - timestamp;

    if (diff <= 0) {
      closesetIndex = i;
      break;
    }

    if (i === timestamps.length - 1) {
      closesetIndex = i;
    }
  }

  return closesetIndex;
}

export function getLatestSnapshot(snapshots: Map<number, any[]>) {
  const lastGroup = snapshots.get(snapshots.size - 1) || [];

  return lastGroup[lastGroup.length - 1] ?? null;
}

export function findHighestCloseItem(array: number[], target: number) {
  let left = 0;
  let right = array.length - 1;
  let result = -1; // Default value if no close item is found

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);

    if (array[mid] <= target) {
      result = array[mid]; // Update the result to the current item
      left = mid + 1; // Look in the right half of the array
    } else {
      right = mid - 1; // Look in the left half of the array
    }
  }

  return result;
}

/**
 * Group consecutive numbers in an array.
 * input: [1, 2, 3, 5, 6, 7, 9, 10, 11]
 * output: [[1, 3], [5, 7], [9, 11]]
 */

export function groupConsecutiveNumbers(sortedArray: number[]) {
  const groups = [];
  let start = sortedArray[0];
  let end = sortedArray[0];

  for (let i = 1; i < sortedArray.length; i++) {
    if (sortedArray[i] === end + 1) {
      end = sortedArray[i];
    } else {
      groups.push([start, end]);
      start = sortedArray[i];
      end = sortedArray[i];
    }
  }

  groups.push([start, end]);

  return groups;
}

export function convertToUtc(date: Date) {
  return date.toISOString();
}

export function getTracks<TSnapshot>(
  results: SnapshotsGroupedByTrackId<TSnapshot>[]
) {
  return results.map((item) => item.track_id);
}

export function handleError(error: unknown) {
  if (error instanceof Error) {
    toast.error(
      error?.message || "Unexpected error occurred. Please, try again!"
    );
  } else {
    toast.error("Unexpected error occurred. Please, try again!");
  }
}

export const createCartSnapshot = (
  decisionSnapshot: DecisionSnapshot,
  prevProducts: UserDecisionPublic[],
  cartTotal: number
): CartSnapshot => {
  // Note that this will only shallow copy the products array
  // Each product object still maintains the old reference
  const newProducts = [...prevProducts];
  const productIndex = prevProducts.findIndex(
    (sku) => sku.sku_id === decisionSnapshot.sku_id
  );

  if (productIndex === -1) {
    newProducts.push(decisionSnapshot);
  } else {
    const productClone = { ...newProducts[productIndex] };
    productClone.quantity += decisionSnapshot.quantity;
    newProducts[productIndex] = productClone;
  }

  const newCartTotal =
    cartTotal + decisionSnapshot.quantity * decisionSnapshot.retail_price;

  return {
    datetime: decisionSnapshot.time,
    products: newProducts,
    cartTotal: newCartTotal,
  };
};

export const getTimestampIndex = (
  timestamp: Date,
  startTimeAnchor: Date | null,
  bucketDuration = CACHE_RESOLUTION
) => {
  if (!startTimeAnchor) return -1;

  const timestampCopy = new Date(timestamp);

  timestampCopy.setMilliseconds(0);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return Math.round((timestampCopy - startTimeAnchor) / bucketDuration);
};

export const isSnapshotAlreadyExist = <TSnapshot extends SnapshotBase>(
  snapshot: TSnapshot,
  snapshotGroup: TSnapshot[] | undefined
) => {
  if (
    snapshotGroup &&
    snapshotGroup.some((s) => getDateTime(s) === getDateTime(snapshot))
  ) {
    return true;
  }

  return false;
};

export const getDateTime = (s: SnapshotBase) => {
  return "datetime" in s ? s.datetime : s.time;
};

export const groupByTrack = <
  TSnapshot extends SnapshotBase & { track_id: string },
>(
  detections: TSnapshot[]
): SnapshotsGroupedByTrackId<TSnapshot>[] => {
  const detectionsMap = detections.reduce<Record<string, TSnapshot[]>>(
    (acc, detection) => {
      const trackId = detection.track_id || UNMATCHED_DETECTION_TRACK_ID;
      const newDetection = {
        ...detection,
        datetime: getDateTime(detection),
      };
      if (trackId in acc) {
        acc[trackId].push(newDetection);
      } else {
        acc[trackId] = [newDetection];
      }

      return acc;
    },
    {}
  );

  return Object.keys(detectionsMap).map((trackId) => ({
    track_id: trackId,
    snapshots: detectionsMap[trackId],
  })) as any[];
};
